import * as React from 'react'

import {
  Box,
  Icon,
  type MediumIcon,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { debounce } from 'lodash'

import Chip from 'modules/core/components/Filter/base/Chip'
import ChipWithIcon from 'modules/core/components/Filter/base/ChipWithIcon'
import ClearValue from 'modules/core/components/Filter/base/ClearValue'
import Content from 'modules/core/components/Filter/base/Content'
import Provider from 'modules/core/components/Filter/base/Provider'
import useFilter from 'modules/core/components/Filter/base/useFilter'
import 'modules/core/components/Filter/filters/SingleChoice/index.styles.css'
import TextField from 'modules/core/components/TextField'
import { useCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

// we need to receive this parameter in order to know how to coerce
// the value when submiting, otherwise we can't know if we need a string or
// a number, since from the text field we always receive a string.
type FieldType = 'string' | 'number'

type Option<T> = {
  label: string
  value: T
  component?: React.ReactNode
}

type Props<T> = {
  header?: React.ReactNode
  footer?: React.ReactNode
  label: string
  value?: T
  onValueChange: (value?: T) => void
  defaultOpen?: boolean
  icon?: MediumIcon
  fieldType: FieldType
  description?: string
  validation?: (value: string) => boolean
  errorMessage?: string
  options?: Option<T>[]
  fullWidth?: boolean
}

type BodyProps<T> = {
  fieldType: FieldType
  description?: string
  validation?: (value: string) => boolean
  errorMessage?: string
  options?: Option<T>[]
}

function Options<T>({
  options,
  onValueChange,
  value,
}: {
  options?: Option<T>[]
  onValueChange: (value: string) => void
  value: string
}) {
  const [open, setOpen] = React.useState<boolean>(false)
  const iconType = open ? 'caret-up' : 'caret-down'
  if (!options || options.length === 0) return null

  return (
    <Box overflowY="hidden">
      <Box
        onClick={() => setOpen(!open)}
        cursor="pointer"
        flexDirection="row"
        alignItems="center"
        gap="s8"
      >
        <Text size="300" weight="medium">
          {i18n.t('data_table.filters.options')}
        </Text>
        <Icon.Tiny color="grey800" icon={iconType} />
      </Box>
      <Box overflowY="scroll">
        {open &&
          options.map(({ label, value: v, component }, index) => (
            <button
              key={index}
              className={'SingleChoice_option'}
              onClick={() => onValueChange(String(v))}
            >
              {component ?? (
                <Text.Normal size="300" color="grey800" ellipsis>
                  {label}
                </Text.Normal>
              )}
              {value === String(v) && <Icon.Medium icon="check" color="viridian1200" />}
            </button>
          ))}
      </Box>
    </Box>
  )
}

function Body<T>({ fieldType, description, validation, errorMessage, options }: BodyProps<T>) {
  const { value: filterValue, onValueChange } = useFilter()
  const initialValue = filterValue ? String(filterValue) : ''
  const [currentValue, setCurrentValue] = React.useState<string | undefined>(initialValue)
  const [open, toggle] = useToggle()
  const [currentErrorMessage, setErrorMessage] = React.useState<string | undefined>('')

  useEffect(() => {
    validateValue(initialValue)
  }, [])

  const coerceValue = (value: string) => {
    if (fieldType === 'string') return value || undefined

    if (!value) return undefined
    return Number(value)
  }

  const validateValue = (value: string) => {
    if (!validation) return true
    if (validation && !validation(value) && value !== '') {
      setErrorMessage(errorMessage || ' ')
      updateSearch.cancel()
      return false
    }

    if (currentErrorMessage !== '') setErrorMessage('')
    return true
  }

  const handleOnChange = (value: string) => {
    setCurrentValue(value)

    if (!validateValue(value)) {
      return
    }

    updateSearch(value)
  }

  const handleCoerceValue = (value: string) => onValueChange(coerceValue(value))

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateSearch = useCallback(
    debounce((value: string) => handleCoerceValue(value), 1000),
    []
  )

  return (
    <Box flexDirection="column" padding="s16" gap="s16" textAlign="center" overflowY="hidden">
      <Box>
        <Tooltip.Alt
          label={description}
          open={!!description && open}
          attachment="bottom center"
          toggle={toggle}
        >
          <TextField
            value={String(currentValue || '')}
            onChange={(value) => handleOnChange(value)}
            error={currentErrorMessage}
          />
        </Tooltip.Alt>
      </Box>
      <Options
        options={options}
        onValueChange={handleCoerceValue}
        value={String(currentValue || '')}
      />
    </Box>
  )
}

function FreeField<T>({
  header,
  label,
  value,
  onValueChange,
  defaultOpen,
  footer,
  fieldType,
  description,
  errorMessage,
  validation,
  options,
  icon,
  fullWidth,
}: Props<T>) {
  return (
    <Provider value={value} onValueChange={onValueChange} defaultOpen={!!defaultOpen}>
      {icon ? (
        <ChipWithIcon label={label} activeLabel={String(value)} icon={icon} />
      ) : (
        <Chip.Toggler
          active={!!value}
          label={label}
          activeLabel={String(value)}
          fullWidth={fullWidth}
        />
      )}
      <Content
        header={header}
        body={
          <Body
            fieldType={fieldType}
            description={description}
            errorMessage={errorMessage}
            validation={validation}
            options={options}
          />
        }
        footer={<ClearValue />}
      />
    </Provider>
  )
}

export default FreeField
